import { api } from '../api';

/** What an upload is granted against. A comment's images are granted against
 *  its task and a new task's against its project, because the thing they will
 *  sit in does not exist yet; the server lets a body embed any image its
 *  writer was granted, whatever it was granted against. */
export interface Owner {
  type: 'project' | 'task';
  id: number;
}

/** Anything bigger is not a screenshot, and belongs in a file. */
const maxImageBytes = 20 * 1024 * 1024;

export const isImage = (file: File) => file.type.startsWith('image/');

/** Puts one image in the bucket and returns the src it will be reached by. */
export async function uploadImage(file: File, owner: Owner): Promise<string> {
  if (file.size > maxImageBytes) throw new Error(`${file.size} bytes is over the ${maxImageBytes} limit`);
  const grant = await api.presignUpload({ filename: keyName(file), ownerType: owner.type, ownerId: owner.id });
  // A fetch the page's Content-Security-Policy or the bucket's CORS refuses
  // rejects with a bare TypeError; say where it was going.
  const response = await fetch(grant.url, {
    method: grant.method,
    body: file,
    // Not signed, so S3 takes it as given and serves the image back with it.
    headers: file.type ? { 'Content-Type': file.type } : undefined,
  }).catch((err: unknown) => {
    throw new Error(`the browser did not send it to ${new URL(grant.url).origin} (blocked by CSP or CORS?)`, { cause: err });
  });
  if (!response.ok) {
    // S3 says what it refused in the body, as <Code>SignatureDoesNotMatch</Code>.
    const body = await response.text().catch(() => '');
    const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1];
    throw new Error(`S3 answered ${response.status}${code ? ` ${code}` : ''}`);
  }
  return '/assets/inline/' + grant.key.split('/').map(encodeSegment).join('/');
}

/** The image as a data: URL, which is how it is shown while it uploads. The
 *  policy in front of the app allows data: in img-src and not blob:, and
 *  every blob: preview came out as a broken image. */
export function dataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** A pasted screenshot arrives as "image.png", or with no name at all. */
export function nameOf(file: File): string {
  const name = file.name.replace(/[[\]()\n]/g, '').trim();
  return name || `image.${file.type.split('/')[1] ?? 'png'}`;
}

/** The name the object is stored under. "Screenshot 2026-10-13 at
 *  11.12.00.png" makes a key full of %20s; the readable name goes in the alt,
 *  and the key only has to be unique, which its timestamp already is. */
function keyName(file: File): string {
  const name = nameOf(file);
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
  const stem = (dot > 0 ? name.slice(0, dot) : name)
    .toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24)
    .replace(/-+$/, '');
  return `${stem || 'image'}.${ext || file.type.split('/')[1] || 'png'}`;
}

/** encodeURIComponent leaves brackets and quotes alone. */
function encodeSegment(segment: string): string {
  return encodeURIComponent(segment).replace(/[!'()*]/g,
    (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase());
}
